import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarPlus, Loader2, MessageSquare, Phone, Trash2, RotateCcw, ShieldAlert, Pencil, XCircle, ClipboardPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMeta } from "@/hooks/useMeta";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { StatusBadge } from "./StatusBadge";
import { Btn, FieldRow, LabeledSelect } from "./Ui";
import { AssignAppointmentDialog } from "./AssignAppointmentDialog";
import { EditSubmissionDialog } from "./EditSubmissionDialog";

export function SubmissionDetailSheet({ id, onClose }) {
  const qc = useQueryClient();
  const { isSupervisor, user } = useAuth();
  const { data: meta } = useMeta();
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [hardDeleteStep, setHardDeleteStep] = useState(0);
  const [confirmText, setConfirmText] = useState("");
  const [cancelAppt, setCancelAppt] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["submission", id],
    queryFn: async () => (await api.get(`/submissions/${id}`)).data,
    enabled: !!id,
  });
  const sub = data?.submission;
  const appointments = data?.appointments || [];
  const activeAppt = appointments.find((a) => a.status === "scheduled");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["submission", id] });
    qc.invalidateQueries({ queryKey: ["submissions"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["appointments"] });
  };

  const statusMutation = useMutation({
    mutationFn: async (status) => (await api.patch(`/submissions/${id}/status`, { status })).data,
    onSuccess: () => {
      toast.success("Status updated");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const noteMutation = useMutation({
    mutationFn: async (text) => (await api.post(`/submissions/${id}/notes`, { text })).data,
    onSuccess: () => {
      setNoteText("");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => (await api.delete(`/submissions/${id}`)).data,
    onSuccess: () => {
      toast.success("Submission moved to trash");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const restoreMutation = useMutation({
    mutationFn: async () => (await api.post(`/submissions/${id}/restore`)).data,
    onSuccess: () => {
      toast.success("Submission restored");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const hardDeleteMutation = useMutation({
    mutationFn: async () => (await api.post(`/submissions/${id}/hard-delete`, { confirm_text: confirmText })).data,
    onSuccess: () => {
      toast.success("Submission permanently deleted");
      setHardDeleteStep(0);
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const cancelMutation = useMutation({
    mutationFn: async (appt) => (await api.post(`/appointments/${appt.id}/cancel`, { reason: "Cancelled from submission" })).data,
    onSuccess: () => {
      toast.success("Appointment cancelled - admin notified");
      setCancelAppt(null);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const canEditAppt = (a) => isSupervisor || a.admin_id === user?.id;
  const label = (dict, key) => (key ? meta?.[dict]?.[key] || key : "-");

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-xl" data-testid="submission-detail-sheet">
        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {error && <div className="p-6 text-sm text-destructive">{errorMessage(error)}</div>}
        {sub && (
          <>
            <SheetHeader className="border-b border-border px-6 pb-4 pt-6 text-start">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={sub.status} data-testid="submission-status-badge" />
                <span className="text-xs text-muted-foreground">Received {fmtRelative(sub.created_at)}</span>
                {sub.deleted_at && <StatusBadge status="failed" label="In trash" />}
              </div>
              <SheetTitle className="text-2xl" data-testid="submission-detail-name">
                {sub.beneficiary_name}
              </SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span>
                  {label("services", sub.service)}
                  {sub.assessment_type ? ` - ${label("assessment_types", sub.assessment_type)}` : ""}
                </span>
                <a href={`tel:${sub.phone}`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline" dir="ltr">
                  <Phone className="h-3.5 w-3.5" /> {sub.phone}
                </a>
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/40 px-6 py-3">
              <LabeledSelect id="status-select" value={sub.status} onChange={(e) => statusMutation.mutate(e.target.value)} className="min-w-[170px]" data-testid="submission-status-select" disabled={statusMutation.isPending || !!sub.deleted_at}>
                {(meta?.statuses || []).map((s) => (
                  <option key={s} value={s}>
                    {meta?.status_labels?.[s] || s}
                  </option>
                ))}
              </LabeledSelect>
              {isSupervisor && !sub.deleted_at && (
                <Btn onClick={() => setAssignOpen(true)} data-testid="submission-assign-appointment-button">
                  <CalendarPlus /> {activeAppt ? "Add another" : "Assign appointment"}
                </Btn>
              )}
              {isSupervisor && !sub.deleted_at && (
                <Btn variant="secondary" onClick={() => setEditOpen(true)} data-testid="submission-edit-button">
                  <Pencil /> Edit details
                </Btn>
              )}
              {isSupervisor && (
                <Btn
                  variant="secondary"
                  onClick={() => window.open("/supervisorintake", "_blank", "noopener")}
                  title="Fill in the intake form yourself, in a new tab"
                  data-testid="submission-new-intake-button"
                >
                  <ClipboardPlus /> New intake form
                  <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                </Btn>
              )}
              <div className="ms-auto flex gap-1">
                {!sub.deleted_at ? (
                  <Btn variant="ghost" size="icon" title="Move to trash" onClick={() => deleteMutation.mutate()} data-testid="submission-delete-button">
                    <Trash2 className="text-muted-foreground" />
                  </Btn>
                ) : (
                  isSupervisor && (
                    <>
                      <Btn variant="secondary" size="sm" onClick={() => restoreMutation.mutate()} data-testid="submission-restore-button">
                        <RotateCcw /> Restore
                      </Btn>
                      <Btn variant="dangerOutline" size="sm" onClick={() => setHardDeleteStep(1)} data-testid="submission-hard-delete-button">
                        <ShieldAlert /> Delete forever
                      </Btn>
                    </>
                  )
                )}
              </div>
            </div>

            <Tabs defaultValue="details" className="flex min-h-0 flex-1 flex-col">
              <TabsList className="mx-6 mt-4 grid h-10 grid-cols-3 rounded-[10px] bg-secondary p-1">
                <TabsTrigger value="details" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="submission-tab-details">
                  Details
                </TabsTrigger>
                <TabsTrigger value="notes" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="submission-tab-notes">
                  Notes {sub.notes?.length ? <span className="ms-1 rounded-full bg-primary/10 px-1.5 text-[11px] text-primary">{sub.notes.length}</span> : null}
                </TabsTrigger>
                <TabsTrigger value="activity" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="submission-tab-activity">
                  Activity
                </TabsTrigger>
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                <TabsContent value="details" className="mt-0 space-y-5">
                  <section>
                    <p className="section-label mb-1">Appointment</p>
                    {appointments.length === 0 && <p className="text-sm text-muted-foreground">No appointment yet.{isSupervisor ? " Use “Assign appointment” to schedule one." : ""}</p>}
                    {appointments.map((a) => (
                      <div key={a.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-[10px] border border-border p-3 text-sm" data-testid="submission-appointment-row">
                        <div>
                          <p className="font-semibold text-foreground">{fmtDateTime(a.start)} - {a.duration_minutes} min</p>
                          <p className="text-muted-foreground">
                            {label("appointment_types", a.type)} with <span className="font-medium text-foreground">{a.admin_name}</span>
                          </p>
                          {a.note && <p className="mt-1 text-xs text-muted-foreground">“{a.note}”</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <StatusBadge status={a.status === "scheduled" ? "scheduled" : a.status === "completed" ? "completed" : "cancelled"} label={a.status} />
                          {a.status === "scheduled" && canEditAppt(a) && (
                            <>
                              {isSupervisor && (
                                <Btn variant="ghost" size="icon" title="Edit" onClick={() => setEditAppt(a)} data-testid="submission-appointment-edit-button">
                                  <Pencil />
                                </Btn>
                              )}
                              <Btn variant="ghost" size="icon" title="Cancel appointment" onClick={() => setCancelAppt(a)} data-testid="submission-appointment-cancel-button">
                                <XCircle className="text-destructive" />
                              </Btn>
                            </>
                          )}
                          <Link to={`/dashboard/schedule?appointment=${a.id}`} className="text-xs font-medium text-primary hover:underline">
                            Open
                          </Link>
                        </div>
                      </div>
                    ))}
                  </section>

                  <section>
                    <p className="section-label mb-1">Beneficiary</p>
                    <dl className="divide-y divide-border">
                      <FieldRow label="Full name">{sub.beneficiary_name}</FieldRow>
                      <FieldRow label="Age">{sub.age != null ? `${sub.age} years` : "-"}{sub.dob ? ` (born ${sub.dob})` : ""}</FieldRow>
                      <FieldRow label="Language">{label("languages", sub.language)}</FieldRow>
                    </dl>
                  </section>
                  <section>
                    <p className="section-label mb-1">Contact</p>
                    <dl className="divide-y divide-border">
                      <FieldRow label="Contact person">{sub.contact_name}</FieldRow>
                      <FieldRow label="Relationship">
                        {label("relationships", sub.relationship)}
                        {sub.relationship_other ? ` - ${sub.relationship_other}` : ""}
                      </FieldRow>
                      <FieldRow label="Phone">
                        <span dir="ltr">{sub.phone}</span>
                      </FieldRow>
                      <FieldRow label="Preferred time">
                        {label("contact_times", sub.preferred_contact_time)}
                        {sub.preferred_time_specific ? ` - ${sub.preferred_time_specific}` : ""}
                      </FieldRow>
                    </dl>
                  </section>
                  <section>
                    <p className="section-label mb-1">Request</p>
                    <dl className="divide-y divide-border">
                      <FieldRow label="Service">
                        {label("services", sub.service)}
                        {sub.service_other ? ` - ${sub.service_other}` : ""}
                      </FieldRow>
                      {sub.assessment_type && (
                        <FieldRow label="Assessment">
                          {label("assessment_types", sub.assessment_type)}
                          {sub.assessment_other ? ` - ${sub.assessment_other}` : ""}
                        </FieldRow>
                      )}
                      <FieldRow label="Referral source">
                        {label("referral_sources", sub.referral_source)}
                        {sub.referral_other ? ` - ${sub.referral_other}` : ""}
                      </FieldRow>
                      <FieldRow label="Assigned admin">{sub.assigned_admin_name || "Unassigned"}</FieldRow>
                      <FieldRow label="Source">
                        {sub.source === "whatsapp_link"
                          ? `WhatsApp link${sub.link_phone ? ` (sent to ${sub.link_phone})` : ""}`
                          : sub.source === "supervisor"
                            ? "Filled in by a supervisor"
                            : "Organic / shared link"}
                      </FieldRow>
                      {sub.created_by_name && (
                        <FieldRow label="Created by">
                          <span className="font-semibold text-foreground" data-testid="submission-created-by">
                            {sub.created_by_name}
                          </span>
                        </FieldRow>
                      )}
                      {sub.edited_by_name && (
                        <FieldRow label="Last edited">
                          <span data-testid="submission-edited-by">
                            {sub.edited_by_name} · {fmtDateTime(sub.edited_at)}
                          </span>
                        </FieldRow>
                      )}
                      <FieldRow label="Received">{fmtDateTime(sub.created_at)}</FieldRow>
                      <FieldRow label="Reference">
                        <span className="font-mono text-xs">{sub.id}</span>
                      </FieldRow>
                    </dl>
                  </section>
                </TabsContent>

                <TabsContent value="notes" className="mt-0">
                  <div className="space-y-3" data-testid="submission-notes-thread">
                    {(sub.notes || []).length === 0 && <p className="text-sm text-muted-foreground">No internal notes yet. Notes are visible to the team only.</p>}
                    {[...(sub.notes || [])].reverse().map((n) => (
                      <div key={n.id} className="rounded-[10px] border border-border bg-secondary/40 p-3" data-testid="submission-note">
                        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="font-semibold text-foreground">{n.author_name}</span>
                          <span>{fmtDateTime(n.created_at)}</span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{n.text}</p>
                      </div>
                    ))}
                  </div>
                  {!sub.deleted_at && (
                    <form
                      className="mt-4 space-y-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (noteText.trim()) noteMutation.mutate(noteText.trim());
                      }}
                    >
                      <textarea rows={3} className="emerge-input !h-auto py-2 text-sm" placeholder="Add an internal note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={2000} data-testid="submission-add-note-textarea" />
                      <div className="flex justify-end">
                        <Btn type="submit" size="sm" disabled={!noteText.trim() || noteMutation.isPending} data-testid="submission-add-note-button">
                          <MessageSquare /> Add note
                        </Btn>
                      </div>
                    </form>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="mt-0">
                  <ol className="relative space-y-4 border-s border-border ps-4">
                    {(sub.status_history || []).map((h, i) => (
                      <li key={`h-${i}`} className="relative text-sm">
                        <span className="absolute -start-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                        <p className="text-foreground">
                          Status set to <StatusBadge status={h.status} className="align-middle" /> by {h.by_name || "Client"}
                        </p>
                        <p className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</p>
                      </li>
                    ))}
                    {(data?.audit || []).map((a) => (
                      <li key={a.id} className="relative text-sm">
                        <span className="absolute -start-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--app-teal))]" />
                        <p className="text-foreground">{a.summary}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.actor_name} - {fmtDateTime(a.created_at)}
                        </p>
                      </li>
                    ))}
                  </ol>
                </TabsContent>
              </div>
            </Tabs>
          </>
        )}

        {sub && <EditSubmissionDialog open={editOpen} onOpenChange={setEditOpen} submission={sub} />}
        {sub && <AssignAppointmentDialog open={assignOpen} onOpenChange={setAssignOpen} submission={sub} />}
        {editAppt && <AssignAppointmentDialog open={!!editAppt} onOpenChange={(o) => !o && setEditAppt(null)} appointment={editAppt} submission={sub} />}

        <AlertDialog open={!!cancelAppt} onOpenChange={(o) => !o && setCancelAppt(null)}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
              <AlertDialogDescription>The assigned admin will receive a cancellation email and the slot will be freed.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => cancelMutation.mutate(cancelAppt)} data-testid="confirm-cancel-appointment-button">
                Cancel appointment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={hardDeleteStep > 0} onOpenChange={(o) => !o && setHardDeleteStep(0)}>
          <AlertDialogContent className="bg-white" data-testid="hard-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{hardDeleteStep === 1 ? "Permanently delete this submission?" : "Final confirmation"}</AlertDialogTitle>
              <AlertDialogDescription>
                {hardDeleteStep === 1
                  ? "This removes the submission and its appointments from the database. This cannot be undone."
                  : "Type DELETE below to confirm permanent deletion."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {hardDeleteStep === 2 && <input className="emerge-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="hard-delete-confirm-input" />}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setHardDeleteStep(0)}>Cancel</AlertDialogCancel>
              {hardDeleteStep === 1 ? (
                <AlertDialogAction
                  className="bg-destructive hover:bg-destructive/90"
                  onClick={(e) => {
                    e.preventDefault();
                    setHardDeleteStep(2);
                  }}
                  data-testid="hard-delete-continue-button"
                >
                  Continue
                </AlertDialogAction>
              ) : (
                <AlertDialogAction
                  className="bg-destructive hover:bg-destructive/90"
                  disabled={confirmText.trim().toUpperCase() !== "DELETE"}
                  onClick={(e) => {
                    e.preventDefault();
                    hardDeleteMutation.mutate();
                  }}
                  data-testid="hard-delete-final-button"
                >
                  Delete permanently
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
